<lvr-heading>{{ 'assignment.edit.title' | transloco }}</lvr-heading>

<lvr-section lvrPadding [padVertical]="16">
	<lvr-section-form>
		<lvr-field [label]="readOnlyStartDateLabel() | transloco">
			<p>{{ readOnlyStartDate() | date: 'dd.MM.yyyy' }}</p>
		</lvr-field>
	</lvr-section-form>
</lvr-section>

<form [formGroup]="form" autocomplete="on" lvrAutoSave="assignment" [entityId]="draftId()" [autoLoad]="false">
	@if (showP2Fields()) {
		<lvr-section>
			<lvr-section-form>
				<!-- Einleitende Stelle -->
				@let initiatorCtrl = form.get('initiatorId');
				<lvr-field [label]="'base.field.initiator' | transloco | appendAsterisk">
					<lvr-searchable-select
						formControlName="initiatorId"
						[options]="initiators()"
						[optionLabel]="initiatorLabel"
						[placeholder]="'base.field.select_please' | transloco"
						[searchPlaceholder]="'base.field.search_placeholder' | transloco"
						[noResultsText]="'base.field.no_results' | transloco"
						[ariaLabel]="'base.field.initiator' | transloco"
						[required]="true"
						[hasError]="hasError(initiatorCtrl, 'required')"
						[errorMessage]="'base.form.error.initiator_required' | transloco"
					></lvr-searchable-select>
				</lvr-field>

				@if (showConsultationStart()) {
					<!-- Datum Start der Beratung -->
					@let consultationCtrl = form.get('consultationStartDate');
					<lvr-field [label]="'assignment.edit.start_consultation' | transloco | appendAsterisk">
						<mat-form-field
							lvrInput
							[lvrError]="hasError(consultationCtrl, 'minDateExceeded', 'matDatepickerParse', 'required')"
						>
							<input
								matInput
								[matDatepicker]="picker"
								formControlName="consultationStartDate"
								placeholder="09.10.2025"
								[max]="maxDate()"
								[min]="minDateP2()"
								[errorStateMatcher]="matcher"
							/>
							@if (hasError(consultationCtrl, 'minDateExceeded')) {
								<mat-error>{{ 'assignment.phase.date_min' | transloco }}</mat-error>
							} @else if (hasError(consultationCtrl, 'matDatepickerParse')) {
								<mat-error>{{
									'base.form.error.date_format_invalid' | transloco: { format: dateFormatHint }
								}}</mat-error>
							} @else if (hasError(consultationCtrl, 'required')) {
								<mat-error>{{ 'base.form.error.required' | transloco }}</mat-error>
							} @else {
								<mat-hint>{{ 'assignment.edit.start_consultation_hint' | transloco }}</mat-hint>
							}
							<mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
							<mat-datepicker #picker></mat-datepicker>
						</mat-form-field>
					</lvr-field>
				}

				@if (!isValidDraft()) {
					@let impedimentReportDueDateCtrl = form.get('impedimentReportDueDate');
					<lvr-field [label]="'assignment.edit.impediment_report_due_date' | transloco">
						<mat-form-field
							lvrInput
							[lvrError]="
								hasError(impedimentReportDueDateCtrl, 'minDateExceeded', 'maxDateExceeded', 'matDatepickerParse')
							"
						>
							<input
								matInput
								[matDatepicker]="picker"
								formControlName="impedimentReportDueDate"
								placeholder="09.10.2025"
								[min]="isImpedimentReportDueDateDirty() ? minImpedimentReportDueDate() : null"
								[max]="isImpedimentReportDueDateDirty() ? maxImpedimentReportDueDate() : null"
								[errorStateMatcher]="matcher"
							/>
							@if (hasError(impedimentReportDueDateCtrl, 'minDateExceeded')) {
								<mat-error>{{ 'base.form.error.minDateExceeded' | transloco }}</mat-error>
							} @else if (hasError(impedimentReportDueDateCtrl, 'matDatepickerParse')) {
								<mat-error>{{
									'base.form.error.date_format_invalid' | transloco: { format: dateFormatHint }
								}}</mat-error>
							} @else if (hasError(impedimentReportDueDateCtrl, 'maxDateExceeded')) {
								<mat-error>{{ 'base.form.error.maxDateExceeded' | transloco }}</mat-error>
							}
							<mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
							<mat-datepicker #picker></mat-datepicker>
						</mat-form-field>
					</lvr-field>
				}

				@if (showRequiredLegalBaseInP2()) {
					<!-- Betreuungsart -->
					@let typeOfCare = form.get('typeOfCareId');
					<lvr-field [label]="'base.field.type_of_care' | transloco | appendAsterisk">
						<mat-form-field
							lvrInput
							[lvrError]="hasError(typeOfCare, 'required')"
							[attr.aria-label]="'base.field.select_please' | transloco"
						>
							<mat-select
								formControlName="typeOfCareId"
								[placeholder]="'base.field.select_please' | transloco"
								aria-required="true"
							>
								@for (option of typesOfCare(); track option.id) {
									<mat-option [value]="option.id">{{ option.remark }}</mat-option>
								}
							</mat-select>
							@if (hasError(typeOfCare, 'required')) {
								<mat-error>{{ 'base.form.error.type_of_care_required' | transloco }}</mat-error>
							}
						</mat-form-field>
					</lvr-field>

					<!-- Auftraggeber -->
					@let sponsorCtrl = form.get('sponsorId');
					<lvr-field [label]="'base.field.sponsor' | transloco | appendAsterisk">
						<lvr-searchable-select
							formControlName="sponsorId"
							[options]="availableSponsor()"
							[optionLabel]="sponsorLabel"
							[placeholder]="'base.field.select_please' | transloco"
							[searchPlaceholder]="'base.field.search_placeholder' | transloco"
							[noResultsText]="'base.field.no_results' | transloco"
							[ariaLabel]="'base.field.sponsor' | transloco"
							[required]="true"
							[hasError]="!!typeOfCare!.value && availableSponsor().length > 0 && hasError(sponsorCtrl, 'required')"
							[errorMessage]="'base.form.error.sponsor_required' | transloco"
							[hintText]="
								!typeOfCare!.value
									? ('base.form.hint.no_type_care_selected' | transloco)
									: availableSponsor().length === 0
										? ('base.form.hint.no_initiators' | transloco)
										: ''
							"
						></lvr-searchable-select>
					</lvr-field>

					<!-- Grundlage -->
					@let legalFoundationCtrl = form.get('legalFoundationId');
					<lvr-field [label]="'base.field.legal_foundation' | transloco | appendAsterisk">
						<lvr-searchable-select
							formControlName="legalFoundationId"
							[options]="availableLegalFoundation()"
							[optionLabel]="legalFoundationLabel"
							[placeholder]="'base.field.select_please' | transloco"
							[searchPlaceholder]="'base.field.search_placeholder' | transloco"
							[noResultsText]="'base.field.no_results' | transloco"
							[ariaLabel]="'base.field.legal_foundation' | transloco"
							[required]="true"
							[hasError]="
								!!sponsorCtrl!.value &&
								availableLegalFoundation().length > 0 &&
								hasError(legalFoundationCtrl, 'required')
							"
							[errorMessage]="'base.form.error.legal_foundation_required' | transloco"
							[hintText]="
								!sponsorCtrl!.value
									? ('base.form.hint.no_sponsor_selected' | transloco)
									: availableLegalFoundation().length === 0
										? ('base.form.hint.no_legal_foundation' | transloco)
										: ('base.field.legal_foundation_hint' | transloco)
							"
						></lvr-searchable-select>
					</lvr-field>

					<!-- Case Group -->
					@if (showCaseGroup()) {
						@let caseGroupCtrl = form.get('caseGroupId');
						<lvr-field [label]="'base.field.case_group' | transloco | appendAsterisk">
							<lvr-searchable-select
								formControlName="caseGroupId"
								[options]="caseGroups()"
								[optionLabel]="caseGroupLabel"
								[placeholder]="'base.field.select_please' | transloco"
								[searchPlaceholder]="'base.field.search_placeholder' | transloco"
								[noResultsText]="'base.field.no_results' | transloco"
								[ariaLabel]="'base.field.case_group' | transloco"
								[required]="true"
								[hasError]="hasError(caseGroupCtrl, 'required')"
								[errorMessage]="'base.form.error.case_group_required' | transloco"
							></lvr-searchable-select>
						</lvr-field>
					}
				}

				@if (showCareRelatedDisabilityTypeInP2()) {
					<!-- Betreuungsrelevante Behinderungsart -->
					@let disabilityKindCtrl = form.get('careRelatedHandicapId');
					<lvr-field [label]="'base.field.type_of_disability_relevant_to_care' | transloco | appendAsterisk">
						<mat-form-field
							lvrInput
							[lvrError]="hasError(disabilityKindCtrl, 'required')"
							[attr.aria-label]="'base.field.select_please' | transloco"
						>
							<mat-select
								#sel="matSelect"
								formControlName="careRelatedHandicapId"
								[placeholder]="'base.field.type_of_disability_relevant_to_care' | transloco"
								aria-required="true"
							>
								<mat-select-trigger>
									{{ getDisabilityKindName(sel.value) }}
									<span class="text-sm differentiation">{{ getDisabilityKindDifferentiation(sel.value) }}</span>
								</mat-select-trigger>

								@for (option of disabilityKinds(); track option.id) {
									<mat-option class="option" [value]="option.id">
										<span class="remark">{{ option.disabilityType?.remark ?? '' }}</span>
										<span class="text-sm">{{ option.categoryDiagnosis?.description ?? '-' }}</span>
									</mat-option>
								}
							</mat-select>
							@if (disabilityKinds().length === 0) {
								<mat-hint>
									{{ 'assignment.edit.no_disability_kinds_prefix' | transloco }}
									<a
										[routerLink]="['/desk/client', client().id, 'disabilities', 'type', 'add']"
										[queryParams]="queryParams"
										(click)="saveDraftBeforeNavigation()"
									>
										{{ 'assignment.edit.no_disability_kinds_link' | transloco }}
									</a>
									{{ 'assignment.edit.no_disability_kinds_suffix' | transloco }}
								</mat-hint>
							}
							@if (hasError(disabilityKindCtrl, 'required')) {
								<mat-error>{{ 'base.form.error.required' | transloco }}</mat-error>
							}
						</mat-form-field>
					</lvr-field>
				}
			</lvr-section-form>
		</lvr-section>
	}

	@if (showP3Fields()) {
		<lvr-section>
			<lvr-section-form>
				<!-- Betreuungsart -->
				@let typeOfCare = form.get('typeOfCareId');
				<lvr-field [label]="'base.field.type_of_care' | transloco | appendAsterisk">
					<mat-form-field
						lvrInput
						[lvrError]="hasError(typeOfCare, 'required')"
						[attr.aria-label]="'base.field.select_please' | transloco"
					>
						<mat-select
							formControlName="typeOfCareId"
							[placeholder]="'base.field.select_please' | transloco"
							aria-required="true"
						>
							@for (option of typesOfCare(); track option.id) {
								<mat-option [value]="option.id">{{ option.remark }}</mat-option>
							}
						</mat-select>
						@if (hasError(typeOfCare, 'required')) {
							<mat-error>{{ 'base.form.error.type_of_care_required' | transloco }}</mat-error>
						}
					</mat-form-field>
				</lvr-field>

				<!-- Auftraggeber -->
				@let sponsorCtrl = form.get('sponsorId');
				<lvr-field [label]="'base.field.sponsor' | transloco | appendAsterisk">
					<lvr-searchable-select
						formControlName="sponsorId"
						[options]="availableSponsor()"
						[optionLabel]="sponsorLabel"
						[placeholder]="'base.field.select_please' | transloco"
						[searchPlaceholder]="'base.field.search_placeholder' | transloco"
						[noResultsText]="'base.field.no_results' | transloco"
						[ariaLabel]="'base.field.sponsor' | transloco"
						[required]="true"
						[hasError]="!!typeOfCare!.value && availableSponsor().length > 0 && hasError(sponsorCtrl, 'required')"
						[errorMessage]="'base.form.error.sponsor_required' | transloco"
						[hintText]="
							!typeOfCare!.value
								? ('base.form.hint.no_type_care_selected' | transloco)
								: availableSponsor().length === 0
									? ('base.form.hint.no_initiators' | transloco)
									: ''
						"
					></lvr-searchable-select>
				</lvr-field>

				<!-- Grundlage -->
				@let legalFoundationCtrl = form.get('legalFoundationId');
				<lvr-field [label]="'base.field.legal_foundation' | transloco | appendAsterisk">
					<lvr-searchable-select
						formControlName="legalFoundationId"
						[options]="availableLegalFoundation()"
						[optionLabel]="legalFoundationLabel"
						[placeholder]="'base.field.select_please' | transloco"
						[searchPlaceholder]="'base.field.search_placeholder' | transloco"
						[noResultsText]="'base.field.no_results' | transloco"
						[ariaLabel]="'base.field.legal_foundation' | transloco"
						[required]="true"
						[hasError]="
							!!sponsorCtrl!.value && availableLegalFoundation().length > 0 && hasError(legalFoundationCtrl, 'required')
						"
						[errorMessage]="'base.form.error.legal_foundation_required' | transloco"
						[hintText]="
							!sponsorCtrl!.value
								? ('base.form.hint.no_sponsor_selected' | transloco)
								: availableLegalFoundation().length === 0
									? ('base.form.hint.no_legal_foundation' | transloco)
									: ('base.field.legal_foundation_hint' | transloco)
						"
					></lvr-searchable-select>
				</lvr-field>

				<!--  Unemployability Since -->
				@let unemployabilitySinceCtrl = form.get('unemployabilitySince');
				<lvr-field [label]="'base.field.unemployability_since' | transloco">
					<mat-form-field
						lvrInput
						[lvrError]="
							hasError(unemployabilitySinceCtrl, 'matDatepickerParse', 'matDatepickerMax', 'matDatepickerMin')
						"
					>
						<input
							matInput
							[matDatepicker]="pickerIncapacityForWorkSince"
							[max]="maxDate()"
							[min]="minDateIncapacityForWorkSince()"
							formControlName="unemployabilitySince"
							placeholder="01.04.2001"
							[errorStateMatcher]="matcher"
						/>
						@if (hasError(unemployabilitySinceCtrl, 'matDatepickerParse')) {
							<mat-error>{{ 'base.form.error.date_format_invalid' | transloco: { format: dateFormatHint } }}</mat-error>
						} @else if (hasError(unemployabilitySinceCtrl, 'matDatepickerMin')) {
							<mat-error>{{
								'base.form.error.min_years_error' | transloco: { min: incapacityForWorkLimitYears }
							}}</mat-error>
						} @else if (hasError(unemployabilitySinceCtrl, 'matDatepickerMax')) {
							<mat-error>{{ 'base.form.error.date_max_error' | transloco }}</mat-error>
						} @else {
							<mat-hint>{{ dateFormatHint }}</mat-hint>
						}
						<mat-datepicker-toggle matIconSuffix [for]="pickerIncapacityForWorkSince"></mat-datepicker-toggle>
						<mat-datepicker #pickerIncapacityForWorkSince></mat-datepicker>
					</mat-form-field>
				</lvr-field>

				<!-- Case Group -->
				@if (showCaseGroup()) {
					@let caseGroupCtrl = form.get('caseGroupId');
					<lvr-field [label]="'base.field.case_group' | transloco | appendAsterisk">
						<lvr-searchable-select
							formControlName="caseGroupId"
							[options]="caseGroups()"
							[optionLabel]="caseGroupLabel"
							[placeholder]="'base.field.select_please' | transloco"
							[searchPlaceholder]="'base.field.search_placeholder' | transloco"
							[noResultsText]="'base.field.no_results' | transloco"
							[ariaLabel]="'base.field.case_group' | transloco"
							[required]="true"
							[hasError]="hasError(caseGroupCtrl, 'required')"
							[errorMessage]="'base.form.error.case_group_required' | transloco"
						></lvr-searchable-select>
					</lvr-field>
				}

				<!-- Beginn der Beauftragung (P3) -->
				@let startCareCtrl = form.get('careStartDate');
				<lvr-field [label]="'assignment.edit.start_care' | transloco | appendAsterisk">
					<mat-form-field
						lvrInput
						[lvrError]="hasError(startCareCtrl, 'minDateExceeded', 'matDatepickerParse', 'required')"
					>
						<input
							matInput
							[matDatepicker]="carePicker"
							formControlName="careStartDate"
							placeholder="09.10.2025"
							[max]="maxDate()"
							[min]="minDateP3()"
							[errorStateMatcher]="matcher"
						/>
						@if (hasError(startCareCtrl, 'minDateExceeded')) {
							<mat-error>{{ 'assignment.phase.date_min' | transloco }}</mat-error>
						} @else if (hasError(startCareCtrl, 'matDatepickerParse')) {
							<mat-error>{{ 'base.form.error.date_format_invalid' | transloco: { format: dateFormatHint } }}</mat-error>
						} @else if (hasError(startCareCtrl, 'required')) {
							<mat-error>{{ 'assignment.edit.start_care_hint' | transloco }}</mat-error>
						} @else {
							<mat-hint>{{ 'assignment.edit.start_care_hint' | transloco }}</mat-hint>
						}
						<mat-datepicker-toggle matIconSuffix [for]="carePicker"></mat-datepicker-toggle>
						<mat-datepicker #carePicker></mat-datepicker>
					</mat-form-field>
				</lvr-field>

				<!-- Betreuungsrelevante Behinderungsart -->
				@let disabilityKindCtrl = form.get('careRelatedHandicapId');
				<lvr-field [label]="'base.field.type_of_disability_relevant_to_care' | transloco | appendAsterisk">
					<mat-form-field
						lvrInput
						[lvrError]="hasError(disabilityKindCtrl, 'required')"
						[attr.aria-label]="'base.field.select_please' | transloco"
					>
						<mat-select
							#sel="matSelect"
							formControlName="careRelatedHandicapId"
							[placeholder]="'base.field.type_of_disability_relevant_to_care' | transloco"
							aria-required="true"
						>
							<mat-select-trigger>
								{{ getDisabilityKindName(sel.value) }}
								<span class="text-sm differentiation">{{ getDisabilityKindDifferentiation(sel.value) }}</span>
							</mat-select-trigger>

							@for (option of disabilityKinds(); track option.id) {
								<mat-option class="option" [value]="option.id">
									<span class="remark">{{ option.disabilityType?.remark ?? '' }}</span>
									<span class="text-sm">{{ option.categoryDiagnosis?.description ?? '-' }}</span>
								</mat-option>
							}
						</mat-select>
						@if (disabilityKinds().length === 0) {
							<mat-hint>
								{{ 'assignment.edit.no_disability_kinds_prefix' | transloco }}
								<a
									[routerLink]="['/desk/client', client().id, 'disabilities', 'type', 'add']"
									[queryParams]="queryParams"
									(click)="saveDraftBeforeNavigation()"
								>
									{{ 'assignment.edit.no_disability_kinds_link' | transloco }}
								</a>
								{{ 'assignment.edit.no_disability_kinds_suffix' | transloco }}
							</mat-hint>
						}
						@if (hasError(disabilityKindCtrl, 'required')) {
							<mat-error>{{ 'base.form.error.required' | transloco }}</mat-error>
						}
					</mat-form-field>
				</lvr-field>
			</lvr-section-form>
		</lvr-section>
	}

	@if (showP2Fields()) {
		<lvr-section lvrPadding [padVertical]="16">
			<lvr-section-form>
				<!-- Document upload -->
				@let documentsCtrl = form.get('documents');
				<lvr-document-upload
					[subjectType]="subjectType"
					[maxFileCount]="maxFileCount"
					[maxFileSize]="maxFileSize"
					[hasError]="hasFileError(documentsCtrl, 'requiredFile', 'maxFiles', 'maxFileSize', 'allowedFileTypes')"
					[multiple]="true"
					[disabled]="!canManageAssignmentDocuments()"
					formControlName="documents"
					[showIcon]="true"
					[ownerId]="client().uuid"
				></lvr-document-upload>

				<!-- Merkblatt Sozialdatenschutz -->
				<lvr-data-protection
					formControlName="clarification"
					[showAsterisk]="showP2Fields()"
					[locked]="isClarificationFinal()"
					[label]="'base.field.social_data_protection_info'"
					[minDateInput]="clarificationMinDate()"
					[maxDateInput]="clarificationMaxDate()"
				></lvr-data-protection>
			</lvr-section-form>
		</lvr-section>
	}

	@if (showP4Fields()) {
		<lvr-section>
			<lvr-section-form>
				@if (fromPhaseId() === DB_PHASES.P3) {
					<!-- Grund für Beendigung -->
					@if (endOfCares().length > 0) {
						@let endOfCareCtrl = form.get('endOfCareId');
						<lvr-field [label]="'assignment.edit.end_of_care' | transloco | appendAsterisk">
							<mat-form-field
								lvrInput
								[lvrError]="hasError(endOfCareCtrl, 'required')"
								[attr.aria-label]="'assignment.edit.end_of_care' | transloco"
							>
								<mat-select formControlName="endOfCareId" [placeholder]="'base.field.select_please' | transloco">
									@for (item of endOfCares(); track item.id) {
										<mat-option [value]="item.id">{{ item.designation }}</mat-option>
									}
								</mat-select>
								@if (hasError(endOfCareCtrl, 'required')) {
									<mat-error>{{ 'base.form.error.required' | transloco }}</mat-error>
								}
							</mat-form-field>
						</lvr-field>
					}

					<!-- Betreuungsrelevante Behinderungsart -->
					<lvr-field [label]="'base.field.type_of_disability_relevant_to_care' | transloco">
						<mat-form-field lvrInput [attr.aria-label]="'base.field.select_please' | transloco">
							<mat-select
								formControlName="careRelatedHandicapId"
								[placeholder]="'base.field.type_of_disability_relevant_to_care' | transloco"
								aria-required="true"
							>
								@for (option of disabilityKinds(); track option.id) {
									<mat-option [value]="option.id">
										{{ option.disabilityType?.remark ?? '' }}
									</mat-option>
								}
							</mat-select>
							@if (disabilityKinds().length === 0) {
								<mat-hint>
									{{ 'assignment.edit.no_disability_kinds_prefix' | transloco }}
									<a
										[routerLink]="['/desk/client', client().id, 'disabilities', 'type', 'add']"
										[queryParams]="queryParams"
										(click)="saveDraftBeforeNavigation()"
									>
										{{ 'assignment.edit.no_disability_kinds_link' | transloco }}
									</a>
									{{ 'assignment.edit.no_disability_kinds_suffix' | transloco }}
								</mat-hint>
							}
						</mat-form-field>
					</lvr-field>
				}

				<!-- Datum Ende der Betreuung -->
				@let cancelCtrl = form.get('cancellationDate');
				<lvr-field [label]="'assignment.edit.cancellation_date' | transloco | appendAsterisk">
					<mat-form-field
						lvrInput
						[lvrError]="hasError(cancelCtrl, 'minDateExceeded', 'matDatepickerMax', 'matDatepickerParse', 'required')"
					>
						<input
							matInput
							[matDatepicker]="cancelPicker"
							formControlName="cancellationDate"
							placeholder="09.10.2025"
							[max]="maxDate()"
							[min]="minDateP4()"
							[errorStateMatcher]="matcher"
						/>
						@if (hasError(cancelCtrl, 'minDateExceeded')) {
							<mat-error>{{ 'assignment.phase.date_min' | transloco }}</mat-error>
						} @else if (hasError(cancelCtrl, 'matDatepickerParse')) {
							<mat-error>{{ 'base.form.error.date_format_invalid' | transloco: { format: dateFormatHint } }}</mat-error>
						} @else if (hasError(cancelCtrl, 'required')) {
							<mat-error>{{ 'assignment.edit.cancellation_date_hint' | transloco }}</mat-error>
						} @else if (hasError(cancelCtrl, 'matDatepickerMax')) {
							<mat-error>{{ 'base.form.error.date_max_error' | transloco }}</mat-error>
						} @else {
							<mat-hint>{{ 'assignment.edit.cancellation_date_hint' | transloco }}</mat-hint>
						}
						<mat-datepicker-toggle matIconSuffix [for]="cancelPicker"></mat-datepicker-toggle>
						<mat-datepicker #cancelPicker></mat-datepicker>
					</mat-form-field>
				</lvr-field>

				<!-- Document upload -->
				@let documentsCtrl = form.get('documents');
				<lvr-document-upload
					[subjectType]="subjectType"
					[maxFileCount]="maxFileCount"
					[maxFileSize]="maxFileSize"
					[hasError]="hasFileError(documentsCtrl, 'requiredFile', 'maxFiles', 'maxFileSize', 'allowedFileTypes')"
					[multiple]="true"
					[disabled]="!canManageAssignmentDocuments()"
					formControlName="documents"
					[showIcon]="true"
					[ownerId]="client().uuid"
				></lvr-document-upload>
			</lvr-section-form>
		</lvr-section>
	}

	<p lvrPadding [padHorizontal]="0" [padVertical]="24">{{ 'base.form.required' | transloco }}</p>

	<!-- Actions -->
	<lvr-default-actions
		[disableSave]="saveDisabled() || !canEditAssignmentsPermission()"
		(save)="editAssignment()"
		(cancelSave)="cancel()"
		[showLoading]="isLoading()"
	>
		<lvr-restore-actions entityType="assignment" [entityId]="draftId()"></lvr-restore-actions>
	</lvr-default-actions>
</form>
